import React, { useRef, useState } from "react";
import { cn } from "../../utils/cn";
import Icon from "../AppIcon";

const FileUpload = React.forwardRef(({
    className,
    label,
    description,
    error,
    required = false,
    id,
    accept,
    maxSize, // New prop: maximum file size in bytes
    onChange,
    onRemove,
    onPreview,
    value, // This will be the file object
    ...props
}, ref) => {
    const fileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const [validationError, setValidationError] = useState(null);
    
    // Generate unique ID if not provided
    const inputId = id || `file-upload-${Math.random().toString(36).substr(2, 9)}`;

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] || null;
        
        // Real-time validation
        if (file && maxSize && file.size > maxSize) {
            setValidationError(`File size must be less than ${formatFileSize(maxSize)}`);
            // Clear the input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return; // Don't call onChange
        }
        
        setValidationError(null);
        if (onChange) {
            onChange(file);
        }
    };

    const handleRemoveFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (onRemove) {
            onRemove();
        }
    };

    const handlePreview = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (onPreview && value) {
            onPreview(value);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            
            // Real-time validation for drag & drop
            if (file && maxSize && file.size > maxSize) {
                setValidationError(`File size must be less than ${formatFileSize(maxSize)}`);
                return; // Don't call onChange
            }
            
            setValidationError(null);
            if (onChange) {
                onChange(file);
            }
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="space-y-2">
            {label && (
                <label
                    htmlFor={inputId}
                    className={cn(
                        "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                        error ? "text-destructive" : "text-foreground"
                    )}
                >
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                </label>
            )}

            <div
                className={cn(
                    "relative border-2 border-dashed rounded-lg p-6 transition-colors",
                    dragActive 
                        ? "border-primary bg-primary/5" 
                        : error 
                            ? "border-destructive bg-destructive/5" 
                            : "border-border hover:border-primary/50",
                    className
                )}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                {value ? (
                    // File is selected - show file info (relative z-10 so buttons sit above the overlay input)
                    <div className="flex items-center justify-between relative z-10">
                        <div className="flex items-center space-x-3">
                                                         <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                 <Icon name="FileText" size={20} className="text-primary" />
                             </div>
                            <div>
                                <p className="text-sm font-medium text-foreground">
                                    {value.name || value.file_name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {value.size ? formatFileSize(value.size) : 
                                     value.file_size ? formatFileSize(value.file_size) : 'Unknown size'} • {value.type || value.mime_type || 'Unknown type'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {/* Preview button */}
                            <button
                                type="button"
                                onClick={(e) => handlePreview(e)}
                                className="p-2 text-primary hover:bg-primary/10 rounded-md transition-colors"
                                title="Preview document"
                            >
                                <Icon name="Eye" size={16} />
                            </button>
                            {/* Remove button */}
                            <button
                                type="button"
                                onClick={handleRemoveFile}
                                className="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                                title="Remove document"
                            >
                                <Icon name="X" size={16} />
                            </button>
                        </div>
                    </div>
                ) : (
                    // No file selected - show upload area
                    <div className="text-center">
                                                 <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                             <Icon name="UploadCloud" size={24} className="text-muted-foreground" />
                         </div>
                        <p className="text-sm text-muted-foreground mb-2">
                            <span className="font-medium text-primary">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {accept ? `Accepted formats: ${accept}` : 'All file types'}
                            {maxSize && (
                                <span className="block mt-1 font-medium text-foreground">
                                    Max size: {formatFileSize(maxSize)}
                                </span>
                            )}
                        </p>
                    </div>
                )}

                <input
                    ref={(node) => {
                        // Handle both refs
                        if (typeof ref === 'function') {
                            ref(node);
                        } else if (ref) {
                            ref.current = node;
                        }
                        fileInputRef.current = node;
                    }}
                    type="file"
                    id={inputId}
                    accept={accept}
                    onChange={handleFileChange}
                    className={cn(
                        "absolute inset-0 w-full h-full opacity-0",
                        value ? "pointer-events-none" : "cursor-pointer"
                    )}
                    {...props}
                />
            </div>

            {description && !validationError && !error && (
                <p className="text-sm text-muted-foreground">
                    {description}
                </p>
            )}

            {/* Priority: validation error > external error prop */}
            {(validationError || error) && (
                <p className="text-sm text-destructive">
                    {validationError || error}
                </p>
            )}
        </div>
    );
});

FileUpload.displayName = "FileUpload";

export default FileUpload;
